import React, { useState, useMemo, useEffect } from 'react';
import { ActionCenterHeader } from './header/ActionCenterHeader';
import { ActionMonitorSection } from './ActionCards/ActionMonitorSection';
import { ActionAlertSection } from './ActionCards/ActionAlertSection';
import { WorkflowSection } from './WorkflowSections/WorkflowSection';
import { RemediateModal } from './WorkflowSections/RemediateModal';
import { subscribeActivities, fetchActionCenter } from '../../../services/api';
import { startRemediateAction } from '../../../services/remediateActionApi';
import redBG from '../../../Assets/BG-DASHBOARDTILE-1.svg?url';
import styles from './ActionCenter.module.css';

const ActionCenter = () => {
  // State for filters
  const [searchValue, setSearchValue] = useState('');
  const [timeValue, setTimeValue] = useState(() => {
    return Number(localStorage.getItem("actionCenter_timeValue")) || 12;
  });
  const [timeUnit, setTimeUnit] = useState(() => {
    return localStorage.getItem("actionCenter_timeUnit") || "hours";
  });
  const [actionPack, setActionPack] = useState('all');

  // API Data State
  const [activities, setActivities] = useState(null);
  const [actionCenterData, setActionCenterData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Removed error state and connection status - we persist data during disconnections instead of showing errors

  // State for managing expanded alerts (similar to ImpactAnalysis)
  const [expandedAlerts, setExpandedAlerts] = useState(new Set());
  
  // State for remediation progress tracking
  const [remediationProgress, setRemediationProgress] = useState({});

  // State for priority filtering
  const [selectedPriorities, setSelectedPriorities] = useState(new Set());

  // State to track moved alerts to prevent API refreshes from reverting them
  const [movedAlerts, setMovedAlerts] = useState(new Map());

  // Modal State
  const [remediateModal, setRemediateModal] = useState({
    isOpen: false,
    ticket: null,
    isLoading: false
  });

  // Helper functions - copied from ImpactAnalysis
  const getCutoff = (timeValue, timeUnit) => {
    const now = Date.now();
    const unitMs = {
      minutes: 60 * 1000,
      hours: 60 * 60 * 1000,
      days: 24 * 60 * 60 * 1000,
      weeks: 7 * 24 * 60 * 60 * 1000
    };
    return now - timeValue * unitMs[timeUnit];
  };

  const filterByTime = (details, cutoff) => {
    if (!details) return [];
    return details.filter(d => new Date(d.created_at).getTime() >= cutoff);
  };

  // Function to handle alert expansion/collapse (similar to ImpactAnalysis)
  const handleAlertToggle = (alertId) => {
    setExpandedAlerts(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(alertId)) {
        newExpanded.delete(alertId);
      } else {
        newExpanded.add(alertId);
      }
      return newExpanded;
    });
  };

  // Function to format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString();
    } catch (error) {
      return timestamp;
    }
  };

  // Function to render expanded alert details (similar to ImpactAnalysis)
  const renderAlertDetails = (alert) => {
    if (!alert.details || alert.details.length === 0) {
      return (
        <div className={styles.alertDetailsEmpty}>
          No detailed information available for this alert.
        </div>
      );
    }

    // Alert details sorted by created_at descending (newest first)
    const sortedDetails = [...alert.details].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return (
      <div className={styles.alertDetailsContainer}>
        {sortedDetails.map((detail, index) => (
          <div key={detail.id || index} className={styles.alertDetailCard}>
            <div className={styles.alertDetailHeader}>
              <span className={`${styles.alertDetailStatus} ${styles[(detail.event_type || '').toLowerCase()]}`}>
                {(detail.event_type || '').toLowerCase() === 'fired' ? 'Triggered' : detail.event_type}
              </span>
            </div>
            <div className={styles.alertDetailGrid}>
              {/* Created Date and Time */}
              <div className={styles.alertDetailField}>
                <label>Created At</label>
                <span>{formatTimestamp(detail.created_at)}</span>
              </div>
              {/* Priority & Severity */}
              <div className={styles.alertDetailField}>
                <label>Severity/Impact</label>
                <span>{detail.severity} / {detail.impact}</span>
              </div>
            </div>

            {/* Tags */}
            <div className={styles.alertDetailField}>
              <label>Tags</label>
              <div className={styles.tagsContainer}>
                {/* Regular tags */}
                {detail.tags && detail.tags.length > 0 && 
                  detail.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} className={styles.tag}>{tag}</span>
                  ))
                }
                {/* Key-Value tags */}
                {detail.kv_tags && Object.entries(detail.kv_tags).map(([key, value]) => (
                  <span key={key} className={styles.kvTag}>
                    {key}: {value}
                  </span>
                ))}
                {(!detail.tags || detail.tags.length === 0) && 
                 (!detail.kv_tags || Object.keys(detail.kv_tags).length === 0) && 
                 <span className={styles.noTags}>No tags available</span>
                }
              </div>
            </div>
            {/* AI Summary */}
            <div className={styles.alertDetailField}>
              <label>AI Summary</label>
              <p className={styles.aiContent}>{detail.ai_summary || 'No AI summary available'}</p>
            </div>
            {/* AI Business Impact */}
            <div className={styles.alertDetailField}>
              <label>AI Business Impact</label>
              <p className={styles.aiContent}>{detail.ai_business_impact || 'No business impact analysis available'}</p>
            </div>
            {/* AI Reasoning */}
            <div className={styles.alertDetailField}>
              <label>AI Reasoning</label>
              <p className={styles.aiContent}>{detail.ai_reasoning || 'No AI reasoning available'}</p>
            </div>
            {/* Remediate Action Button (only for proposed column and if remediation available) */}
            {detail.remediate_action && detail.remediate_action.action_id && alert.id && alert.id.startsWith('proposed_') && (
              <div className={styles.alertDetailField}>
                <label>Remediation Available</label>
                <button 
                  className={styles.remediateButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemediateAction(detail.remediate_action, alert, detail.id);
                  }}
                  disabled={remediationProgress[detail.remediate_action.action_id]?.isLoading}
                >
                  {remediationProgress[detail.remediate_action.action_id]?.isLoading ? (
                    <div className={styles.loadingContainer}>
                      <div className={styles.spinner}></div>
                      Remediating...
                    </div>
                  ) : (
                    'Remediate'
                  )}
                </button>
              </div>
            )}
            
            {/* Completed Action Button (only for intervention column and if remediation available) */}
            {detail.remediate_action && detail.remediate_action.action_id && alert.id && alert.id.startsWith('intervention_') && (
              <div className={styles.alertDetailField}>
                <label>Mark as Completed</label>
                <button 
                  className={styles.completedButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCompleteAction(detail.remediate_action.action_id, detail.id);
                  }}
                  disabled={remediationProgress[detail.remediate_action.action_id]?.isLoading}
                >
                  {remediationProgress[detail.remediate_action.action_id]?.isLoading ? (
                    <div className={styles.loadingContainer}>
                      <div className={styles.spinner}></div>
                      Completing...
                    </div>
                  ) : (
                    'Completed'
                  )}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Transform action center data into workflow sections based on event-level status
  const transformActionCenterData = (actionCenterData, cutoff, movedAlerts) => {
    if (!actionCenterData?.top_alerts) {
      return {
        intervention: [],
        proposed: [],
        completed: []
      };
    }

    const interventionEvents = [];
    const proposedEvents = [];
    const completedEvents = [];

    // First, handle moved alerts - create individual alert items for completed column
    const processedMovedAlerts = new Set();
    movedAlerts.forEach((movedAlert, key) => {
      if (movedAlert.targetColumn === 'completed') {
        // Find the original event and specific alert detail
        actionCenterData.top_alerts.forEach((event, eventIndex) => {
          const matchingDetail = event.details?.find(detail => 
            detail.id === movedAlert.alertId || 
            detail.id === parseInt(movedAlert.alertId)
          );
          
          if (matchingDetail) {
            // Create individual alert item for completed column
            const completedAlert = {
              name: `${event.alert_title || event.title || `Event ${eventIndex + 1}`} (Resolved)`,
              status: 'Resolved',
              priority: `P${event.priority}`,
              count: { Triggered: 0, Resolved: 1 },
              details: [matchingDetail], // Single alert detail
              impact: event.impact ?? 'none',
              severity: event.severity ?? 'none',
              alert_ids: [movedAlert.alertId], // Individual alert ID
              ticketInfo: event.ticket_no && event.ticket_url ? {
                ticket_no: event.ticket_no,
                ticket_url: event.ticket_url
              } : null,
              id: `completed_moved_${key}`,
              isMovedAlert: true,
              originalEventIndex: eventIndex,
              // Use the actual API status from moved alert
              remediate_action: movedAlert.apiResponse?.remediate_action || event.remediate_action
            };
            
            completedEvents.push(completedAlert);
            processedMovedAlerts.add(`${eventIndex}_${movedAlert.alertId}`);
          }
        });
      }
    });

    actionCenterData.top_alerts.forEach((event, index) => {
      // Filter details by time window
      let filteredDetails = filterByTime(Array.isArray(event.details) ? event.details : [], cutoff);
      
      // Remove moved alerts from their original events
      filteredDetails = filteredDetails.filter(detail => {
        const alertKey = `${index}_${detail.id}`;
        const isMoved = processedMovedAlerts.has(alertKey);
        if (isMoved) {
        }
        return !isMoved;
      });
      
      // Don't skip events that have alert_ids even if no details in time window (after filtering moved alerts)
      // This ensures events with tickets are always shown, unless all their alerts have been moved
      if (filteredDetails.length === 0 && (!event.alert_ids || event.alert_ids.length === 0)) {
        return;
      }

      // Get event-level ticket info (as per requirement)
      let ticketInfo = null;
      if (event.ticket_no && event.ticket_url) {
        ticketInfo = {
          ticket_no: event.ticket_no,
          ticket_url: event.ticket_url
        };
      }

      // Map alert_status for display
      let displayStatus = event.alert_status;
      if ((displayStatus || '').toLowerCase() === 'fired') displayStatus = 'Triggered';

      // Calculate overall counts for the event (use filtered details for time-based counting)
      const { triggeredCount, resolvedCount } = filteredDetails.reduce(
        (acc, detail) => {
          const eventType = (detail.event_type || '').toLowerCase();
          if (eventType === "fired") acc.triggeredCount += 1;
          else if (eventType === "resolved") acc.resolvedCount += 1;
          return acc;
        },
        { triggeredCount: 0, resolvedCount: 0 }
      );

      // Skip events that have no alerts left after filtering moved alerts
      if (filteredDetails.length === 0) {
        return;
      }

      // Create event item with grouped alert_ids
      const eventItem = {
        name: event.alert_title || event.title || `Event ${index + 1}`,
        status: displayStatus,
        priority: `P${event.priority}`,
        count: { Triggered: triggeredCount, Resolved: resolvedCount },
        details: filteredDetails, // Alert details within time window (minus moved alerts)
        impact: event.impact ?? 'none',
        severity: event.severity ?? 'none',
        alert_ids: event.alert_ids ?? [], // Grouped alert IDs from the event
        ticketInfo: ticketInfo, // Event-level ticket info
        remediate_action: event.remediate_action // Pass remediate_action data for pill display
      };

      // Route to appropriate column based on alert_status first, then remediate_action status
      // Priority 1: Check alert_status field first
      const alertStatus = event.alert_status;
      const eventStatus = event.remediate_action?.status;

      if (alertStatus === 'Resolved') {
        // If alert_status is "Resolved", always show in completed column (green)
        completedEvents.push({
          ...eventItem,
          id: `completed_event_${index}`,
          status: 'Resolved'
        });
      } else {
        // If alert_status is not "Resolved", check remediate_action.status
        if (eventStatus === 'SUCCEEDED' || eventStatus === 'COMPLETED') {
          completedEvents.push({
            ...eventItem,
            id: `completed_event_${index}`,
            status: 'Resolved'
          });
        } else if (eventStatus === 'PENDING_APPROVAL') {
          proposedEvents.push({
            ...eventItem,
            id: `proposed_event_${index}`
          });
        } else if (eventStatus === 'RECOMMENDED') {
          interventionEvents.push({
            ...eventItem,
            id: `intervention_event_${index}`
          });
        } else if (eventStatus === 'CREATED') {
          // CREATED status goes to intervention column for review
          interventionEvents.push({
            ...eventItem,
            id: `intervention_event_${index}`
          });
        } else {
          // Default to intervention column for unknown statuses
          interventionEvents.push({
            ...eventItem,
            id: `intervention_event_${index}`
          });
        }
      }
    });

    return {
      intervention: interventionEvents,
      proposed: proposedEvents,
      completed: completedEvents
    };
  };

  // Data transformation functions
  const interactiveMonitorData = (activities, timeValue, timeUnit) => {
    const unitTextMap = {
      minutes: timeValue === 1 ? "minute" : "minutes",
      hours: timeValue === 1 ? "hour" : "hours",
      days: timeValue === 1 ? "day" : "days",
      weeks: timeValue === 1 ? "week" : "weeks",
    };
    const timeText = unitTextMap[timeUnit] || timeUnit;

    // Always return data structure, even when activities is null (for persistent display)
    return [
      {
        label: "Interactive Monitors",
        value: activities?.interactive_monitors?.toString() ?? "0",
        subText: `${activities?.interactive_monitors ?? 0} in last ${timeValue} ${timeText}`,
        background: redBG,
        status: null,
        statusType: null
      },
    ];
  };

  const alertCardsData = (activities, actionCenterData, cutoff) => {
    const alerts = [];

    // Use actionCenterData for alert cards since it has the actual priority data
    // Always return data structure for priorities P1-P4 only (UI requirement)
    for (let i = 1; i <= 4; i++) {
      let openCount = 0;
      let closedCount = 0;

      // Get counts from actionCenterData if available
      if (actionCenterData?.top_alerts) {
        actionCenterData.top_alerts.forEach(alert => {
          if (parseInt(alert.priority) === i) {
            // Use the count from the API response directly
            const firedCount = alert.count?.Fired || 0;
            const resolvedCount = alert.count?.Resolved || 0;
            
            openCount += firedCount;
            closedCount += resolvedCount;
          }
        });
      }

      // Fallback to impact data for P1-P4 if actionCenterData doesn't have them
      // if (openCount === 0 && closedCount === 0) {
      //   const impactAlert = impact?.top_alerts?.find(a => a.priority === i);
      //   const filteredDetails = filterByTime(impactAlert?.details ?? [], cutoff);

      //   openCount = filteredDetails.filter(d => d.event_type === "Fired").length;
      //   closedCount = filteredDetails.filter(d => d.event_type === "Resolved").length;
      // }

      let trend = "balanced";
      if (openCount > closedCount) trend = "up";
      else if (closedCount > openCount) trend = "down";

      alerts.push({
        priority: `P${i}`,
        openCount,
        closedCount,
        trend
      });
    }

    return alerts;
  };

  const cutoff = getCutoff(timeValue, timeUnit);
  const rollingWindowSeconds = timeValue * {
    minutes: 60,
    hours: 3600,
    days: 86400,
    weeks: 604800
  }[timeUnit];

  // Handler for remediate action
  const handleRemediateAction = async (remediateAction, alert, alertId) => {
    const actionId = remediateAction.action_id;
    
    console.log('handleRemediateAction called with:', {
      actionId,
      alertId,
      remediateAction,
      alert
    });
    
    // Check if this is from intervention column (first column)
    if (alert && alert.id && alert.id.startsWith('intervention_')) {
      // For intervention column, directly complete without modal
      handleCompleteAction(actionId, alertId);
      return;
    }
    
    // For proposed column, show modal with remediation description
    setRemediateModal({
      isOpen: true,
      ticket: {
        id: `Action ${actionId}`,
        title: remediateAction.action_title || 'AI Proposed Remediation',
        remediation: remediateAction,
        alertId: alertId // Store the specific alert ID
      },
      isLoading: false
    });
  };

  // Handler for completing action from intervention column
  const handleCompleteAction = async (actionId, alertId) => {
    try {
      console.log('Completing action with action_id:', actionId, 'alert_id:', alertId);
      
      // Set remediation progress state for the button spinner
      setRemediationProgress(prev => ({
        ...prev,
        [actionId]: { isLoading: true, steps: [], status: 'running' }
      }));

      // Start POST + SSE completion action (same API as remediation)
      let apiCallSucceeded = false;
      try {
        apiCallSucceeded = true;
      } catch (apiError) {
        console.warn('Completion setup failed, proceeding with mock behavior:', apiError.message);
        // Don't throw - continue with mock behavior
      }
      
      // Function to complete the action and fetch latest status from API
      const completeAction = async () => {
        setRemediationProgress(prev => ({
          ...prev,
          [actionId]: {
            ...prev[actionId],
            isLoading: false,
            status: 'completed'
          }
        }));
        
        // Fetch latest action center data to get updated status
        try {
          await fetchActionCenter(
            {
              onActionCenter: (data) => {
                console.log("ActionCenter data refreshed after completion:", data);
                
                // Find the event and alert with updated status
                const updatedEvent = data.top_alerts?.find(event => 
                  event.details?.some(detail => 
                    detail.id === alertId || detail.id === parseInt(alertId)
                  )
                );
                
                if (updatedEvent) {
                  // Use the actual API response to determine column placement
                  const alertStatus = updatedEvent.alert_status;
                  const remediateStatus = updatedEvent.remediate_action?.status;
                  
                  // Determine target column based on API status (same logic as transformActionCenterData)
                  let targetColumn = 'intervention'; // default
                  
                  if (alertStatus === 'Resolved') {
                    targetColumn = 'completed';
                  } else if (remediateStatus === 'SUCCEEDED' || remediateStatus === 'COMPLETED') {
                    targetColumn = 'completed';
                  } else if (remediateStatus === 'PENDING_APPROVAL') {
                    targetColumn = 'proposed';
                  }
                  
                  // Move the alert with API response data
                  setMovedAlerts(prev => {
                    const newMovedAlerts = new Map(prev);
                    newMovedAlerts.set(`${actionId}_${alertId}`, {
                      alertId: alertId,
                      actionId: actionId,
                      status: remediateStatus || 'COMPLETED',
                      timestamp: new Date().toISOString(),
                      targetColumn: targetColumn,
                      apiResponse: updatedEvent // Store the complete API response
                    });
                    return newMovedAlerts;
                  });
                } else {
                  // Fallback: move to completed if event not found
                  setMovedAlerts(prev => {
                    const newMovedAlerts = new Map(prev);
                    newMovedAlerts.set(`${actionId}_${alertId}`, {
                      alertId: alertId,
                      actionId: actionId,
                      status: 'COMPLETED',
                      timestamp: new Date().toISOString(),
                      targetColumn: 'completed'
                    });
                    return newMovedAlerts;
                  });
                }
                
                // Update the main action center data
                setActionCenterData(data);
              },
              onError: (error) => {
                console.warn('Failed to refresh action center data after completion:', error);
                // Fallback: move to completed column
                setMovedAlerts(prev => {
                  const newMovedAlerts = new Map(prev);
                  newMovedAlerts.set(`${actionId}_${alertId}`, {
                    alertId: alertId,
                    actionId: actionId,
                    status: 'COMPLETED',
                    timestamp: new Date().toISOString(),
                    targetColumn: 'completed'
                  });
                  return newMovedAlerts;
                });
              }
            },
            rollingWindowSeconds
          );
        } catch (error) {
          console.warn('Failed to fetch updated status, using fallback:', error);
          // Fallback: move to completed column
          setMovedAlerts(prev => {
            const newMovedAlerts = new Map(prev);
            newMovedAlerts.set(`${actionId}_${alertId}`, {
              alertId: alertId,
              actionId: actionId,
              status: 'COMPLETED',
              timestamp: new Date().toISOString(),
              targetColumn: 'completed'
            });
            return newMovedAlerts;
          });
        }
      };

      // Store references for cleanup
      let esComplete;

      // Start POST + SSE completion process (using same API as remediation)
      if (apiCallSucceeded) {
        try {
          esComplete = await startRemediateAction(actionId, alertId, {
            onStep: (stepData) => {
              
              // Check if this step indicates completion
              const stepText = stepData.step || JSON.stringify(stepData);
              if (stepText.includes('All steps completed.')) {         
              }
              
              setRemediationProgress(prev => ({
                ...prev,
                [actionId]: {
                  ...prev[actionId],
                  steps: [...(prev[actionId]?.steps || []), stepData]
                }
              }));
            },
            onComplete: async (data) => {
              
              // Check for completion signal (same logic as remediation)
              let allStepsCompleted = false;
              
              if (data.allStepsCompleted === true) {
                allStepsCompleted = true;
              }
              
              if (data.steps && Array.isArray(data.steps)) {
                const hasCompletionStep = data.steps.some(step => 
                  typeof step === 'string' && step.includes('All steps completed.')
                );
                if (hasCompletionStep) {
                  allStepsCompleted = true;
                }
              }
              
              const completionText = JSON.stringify(data);
              if (completionText.includes('All steps completed.')) {
                allStepsCompleted = true;
              }
              
              await completeAction();
              esComplete?.close();
            },
            onError: async (error) => {
              await completeAction(); // Still complete on error
              esComplete?.close();
            },
            onConnectionChange: (connected) => {
            }
          });

        } catch (sseError) {
          // Set error state without completing
          setRemediationProgress(prev => ({
            ...prev,
            [actionId]: {
              ...prev[actionId],
              isLoading: false,
              status: 'error'
            }
          }));
        }
      } else {
        // API call failed
        setRemediationProgress(prev => ({
          ...prev,
          [actionId]: {
            ...prev[actionId],
            isLoading: false,
            status: 'error'
          }
        }));
      }

    } catch (error) {
      // Ensure we don't leave button in loading state
      setRemediationProgress(prev => ({
        ...prev,
        [actionId]: {
          ...prev[actionId],
          isLoading: false,
          status: 'error'
        }
      }));
    }
  };

  // Handler for priority filter toggle
  const handlePriorityToggle = (priority) => {
    setSelectedPriorities(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(priority)) {
        newSelected.delete(priority);
      } else {
        newSelected.add(priority);
      }
      return newSelected;
    });
  };

  // Persist time settings
  useEffect(() => {
    localStorage.setItem("actionCenter_timeValue", timeValue);
    localStorage.setItem("actionCenter_timeUnit", timeUnit);
  }, [timeValue, timeUnit]);

  // SSE Subscription Effect
  useEffect(() => {
    console.log("ActionCenter SSE useEffect mounted");

    let activitiesReceived = false;
    let impactReceived = false;
    let actionCenterReceived = false;

    const handleError = (source, error) => {
      console.error(`${source} error:`, error);
      // Don't set error state - keep showing previous data during disconnections
      // setError(`${source}: ${error}`);
    };

    // Subscribe to Activities SSE
    const esActivities = subscribeActivities(
      (data) => {
        console.log("ActionCenter Activities SSE received:", data);
        setActivities(data);
        activitiesReceived = true;
        if (actionCenterReceived) {
          console.log("All streams received first message, stopping loader");
          setLoading(false);
        }
      },
      (error) => handleError("Activities", error),
      null, // No connection status tracking needed
      rollingWindowSeconds 
    );

    // Fetch Action Center data (REST API)
    const fetchActionCenterData = async () => {
      try {
        await fetchActionCenter(
          {
            onActionCenter: (data) => {
              setActionCenterData(data);
              actionCenterReceived = true;

              if (activitiesReceived && impactReceived) {
                setLoading(false);
              }
            },
            onError: (error) => handleError("ActionCenter", error)
          },
          rollingWindowSeconds
        );
      } catch (error) {
        handleError("ActionCenter", error.message);
      }
    };

    // Initial fetch
    fetchActionCenterData();

    // Set up periodic refresh for action center data (every 30 seconds)
    const actionCenterInterval = setInterval(fetchActionCenterData, 30000);

    return () => {
      console.log("ActionCenter cleanup");
      clearInterval(actionCenterInterval);
      esActivities?.close();
      // esImpact?.close();
    };
  }, [rollingWindowSeconds]);

  // Removed dateFilter as we're using real-time data filtering instead

  // Transform action center data into workflow data
  const workflowData = useMemo(() => {
    const transformedData = transformActionCenterData(actionCenterData, cutoff, movedAlerts);
    
    return {
      intervention: {
        title: "Recommended User Action",
        subtitle: "Manual investigation required",
        count: transformedData.intervention.length,
        tickets: transformedData.intervention
      },
      proposed: {
        title: "Awaiting Approval",
        subtitle: "Proposed remediation actions available",
        count: transformedData.proposed.length,
        tickets: transformedData.proposed
      },
      completed: {
        title: "Resolved",
        subtitle: "Successfully resolved incidents",
        count: transformedData.completed.length,
        tickets: transformedData.completed
      }
    };
  }, [actionCenterData, cutoff, movedAlerts]);

  // Apply filters to workflow data (including priority filtering)
  const filteredWorkflowData = useMemo(() => {
    // Start with the real data
    const filtered = { ...workflowData };
    
    // Apply text search filter
    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filtered.intervention.tickets = filtered.intervention.tickets.filter(ticket => 
        ticket.name?.toLowerCase().includes(searchLower) ||
        ticket.status?.toLowerCase().includes(searchLower)
      );
      filtered.proposed.tickets = filtered.proposed.tickets.filter(ticket => 
        ticket.name?.toLowerCase().includes(searchLower) ||
        ticket.status?.toLowerCase().includes(searchLower)
      );
      filtered.completed.tickets = filtered.completed.tickets.filter(ticket => 
        ticket.name?.toLowerCase().includes(searchLower) ||
        ticket.status?.toLowerCase().includes(searchLower)
      );
    }

    // Apply priority filter
    if (selectedPriorities.size > 0) {
      filtered.intervention.tickets = filtered.intervention.tickets.filter(ticket => 
        selectedPriorities.has(ticket.priority)
      );
      filtered.proposed.tickets = filtered.proposed.tickets.filter(ticket => 
        selectedPriorities.has(ticket.priority)
      );
      filtered.completed.tickets = filtered.completed.tickets.filter(ticket => 
        selectedPriorities.has(ticket.priority)
      );
    }

    // Update counts after filtering
    filtered.intervention.count = filtered.intervention.tickets.length;
    filtered.proposed.count = filtered.proposed.tickets.length;
    filtered.completed.count = filtered.completed.tickets.length;
    
    return filtered;
  }, [workflowData, searchValue, selectedPriorities]);

  // Handlers for actions
  const handleTicketAction = (ticketId, action, ticket) => {
    console.log(`Action: ${action} on ticket: ${ticketId}`, ticket);
    
    switch(action) {
      case 'remediate':
        setRemediateModal({
          isOpen: true,
          ticket: ticket,
          isLoading: false
        });
        break;
      case 'view':
      case 'details':
        console.log('Opening ticket details:', ticket);
        // Future: Navigate to ticket detail view or open detail modal
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // Modal handlers
  const handleRemediateModalClose = () => {
    setRemediateModal({
      isOpen: false,
      ticket: null,
      isLoading: false
    });
  };

  const handleRemediateApprove = async (actionId, alertId) => {
    setRemediateModal(prev => ({ ...prev, isLoading: true }));
    
    try {
      console.log('Starting remediation action with action_id:', actionId, 'alert_id:', alertId);
      
      // Set remediation progress state for the button spinner first
      setRemediationProgress(prev => ({
        ...prev,
        [actionId]: { isLoading: true, steps: [], status: 'running' }
      }));

      // Close modal immediately
      handleRemediateModalClose();

      // Start POST + SSE remediation action
      let apiCallSucceeded = false;
      try {
        // We'll attempt the POST + SSE connection below
        apiCallSucceeded = true;
      } catch (apiError) {
        console.warn('Remediation setup failed, proceeding with mock behavior:', apiError.message);
        // Don't throw - continue with mock behavior
      }
      
      // Function to complete remediation and move alert based on API status
      const completeRemediation = async () => {
      
        setRemediationProgress(prev => ({
          ...prev,
          [actionId]: {
            ...prev[actionId],
            isLoading: false,
            status: 'completed'
          }
        }));
        
        // Fetch latest action center data to get updated status
        try {
          await fetchActionCenter(
            {
              onActionCenter: (data) => {
                console.log("ActionCenter data refreshed after remediation:", data);
                
                // Find the event and alert with updated status
                const updatedEvent = data.top_alerts?.find(event => 
                  event.details?.some(detail => 
                    detail.id === alertId || detail.id === parseInt(alertId)
                  )
                );
                
                if (updatedEvent) {
                  // Use the actual API response to determine column placement
                  const alertStatus = updatedEvent.alert_status;
                  const remediateStatus = updatedEvent.remediate_action?.status;
                  
                  // Determine target column based on API status (same logic as transformActionCenterData)
                  let targetColumn = 'intervention'; // default
                  
                  if (alertStatus === 'Resolved') {
                    targetColumn = 'completed';
                  } else if (remediateStatus === 'SUCCEEDED' || remediateStatus === 'COMPLETED') {
                    targetColumn = 'completed';
                  } else if (remediateStatus === 'PENDING_APPROVAL') {
                    targetColumn = 'proposed';
                  }
                  
                  // Move the alert with API response data
                  setMovedAlerts(prev => {
                    const newMovedAlerts = new Map(prev);
                    newMovedAlerts.set(`${actionId}_${alertId}`, {
                      alertId: alertId,
                      actionId: actionId,
                      status: remediateStatus || 'COMPLETED',
                      timestamp: new Date().toISOString(),
                      targetColumn: targetColumn,
                      apiResponse: updatedEvent // Store the complete API response
                    });
                    return newMovedAlerts;
                  });
                } else {
                  // Fallback: move to completed if event not found
                  setMovedAlerts(prev => {
                    const newMovedAlerts = new Map(prev);
                    newMovedAlerts.set(`${actionId}_${alertId}`, {
                      alertId: alertId,
                      actionId: actionId,
                      status: 'COMPLETED',
                      timestamp: new Date().toISOString(),
                      targetColumn: 'completed'
                    });
                    return newMovedAlerts;
                  });
                }
                
                // Update the main action center data
                setActionCenterData(data);
              },
              onError: (error) => {
                console.warn('Failed to refresh action center data after remediation:', error);
                // Fallback: move to completed column
                setMovedAlerts(prev => {
                  const newMovedAlerts = new Map(prev);
                  newMovedAlerts.set(`${actionId}_${alertId}`, {
                    alertId: alertId,
                    actionId: actionId,
                    status: 'COMPLETED',
                    timestamp: new Date().toISOString(),
                    targetColumn: 'completed'
                  });
                  return newMovedAlerts;
                });
              }
            },
            rollingWindowSeconds
          );
        } catch (error) {
          console.warn('Failed to fetch updated status, using fallback:', error);
          // Fallback: move to completed column
          setMovedAlerts(prev => {
            const newMovedAlerts = new Map(prev);
            newMovedAlerts.set(`${actionId}_${alertId}`, {
              alertId: alertId,
              actionId: actionId,
              status: 'COMPLETED',
              timestamp: new Date().toISOString(),
              targetColumn: 'completed'
            });
            return newMovedAlerts;
          });
        }
      };

      // Store references for cleanup
      let esRemediate;

      // Start POST + SSE remediation process
      if (apiCallSucceeded) {
        try {
          esRemediate = await startRemediateAction(actionId, alertId, {
            onStep: (stepData) => {
              
              // Check if this step indicates completion
              const stepText = stepData.step || JSON.stringify(stepData);
              if (stepText.includes('All steps completed.')) {
                // Don't complete yet - wait for onComplete callback
              }
              
              setRemediationProgress(prev => ({
                ...prev,
                [actionId]: {
                  ...prev[actionId],
                  steps: [...(prev[actionId]?.steps || []), stepData]
                }
              }));
            },
            onComplete: async (data) => {
              
              // Check multiple ways for completion signal
              let allStepsCompleted = false;
              
              // Check the allStepsCompleted flag from API
              if (data.allStepsCompleted === true) {
                allStepsCompleted = true;
              }
              
              // Check if any step in the data contains "All steps completed."
              if (data.steps && Array.isArray(data.steps)) {
                const hasCompletionStep = data.steps.some(step => 
                  typeof step === 'string' && step.includes('All steps completed.')
                );
                if (hasCompletionStep) {
                  allStepsCompleted = true;
                }
              }
              
              // Check the completion data itself
              const completionText = JSON.stringify(data);
              if (completionText.includes('All steps completed.')) {
                allStepsCompleted = true;
              }
              
              if (allStepsCompleted) {
        
              } else {
              
                await completeRemediation();
              }
              
              if (esRemediate) {
                esRemediate.close();
              }
            },
            onError: async (error) => {
              await completeRemediation(); // Still complete on error
              if (esRemediate) {
                esRemediate.close();
              }
            },
            onConnectionChange: (status) => {
            }
          });
        } catch (startError) {
          // Set error state without completing
          setRemediationProgress(prev => ({
            ...prev,
            [actionId]: {
              ...prev[actionId],
              isLoading: false,
              status: 'error'
            }
          }));
        }
      } else {
        setRemediationProgress(prev => ({
          ...prev,
          [actionId]: {
            ...prev[actionId],
            isLoading: false,
            status: 'error'
          }
        }));
      }
      
    } catch (error) {
      console.error('Unexpected error in remediation flow:', error);
      
      // Set error state but don't stop the remediation process
      setRemediationProgress(prev => ({
        ...prev,
        [actionId]: {
          ...prev[actionId],
          error: error.message
        }
      }));
      
      // Don't close modal here - it's already closed above
      // The fallback timeout will still complete the remediation
    }
  };

  return (
    <div className={styles.container}>
      {/* Header with filters */}
      <ActionCenterHeader
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        timeValue={timeValue}
        onTimeChange={setTimeValue}
        timeUnit={timeUnit}
        onUnitChange={setTimeUnit}
        actionPack={actionPack}
        onActionPackChange={setActionPack}
      />

      {/* Top section - Interactive Monitors and Alert Cards */}
      <div className={styles.topRow}>
        <ActionMonitorSection 
          metrics={interactiveMonitorData(activities, timeValue, timeUnit)} 
          loading={loading && !activities}
        />
        <ActionAlertSection 
          alerts={alertCardsData(activities, actionCenterData, cutoff)} 
          loading={loading && (!activities)}
          selectedPriorities={selectedPriorities}
          onPriorityToggle={handlePriorityToggle}
        />
      </div>

      {/* Bottom section - Workflow columns */}
      <div className={styles.workflowRow}>
        <WorkflowSection
          title={filteredWorkflowData.intervention.title}
          subtitle={filteredWorkflowData.intervention.subtitle}
          count={filteredWorkflowData.intervention.count}
          tickets={filteredWorkflowData.intervention.tickets}
          type="intervention"
          onAction={handleTicketAction}
          expandedAlerts={expandedAlerts}
          onAlertToggle={handleAlertToggle}
          renderAlertDetails={renderAlertDetails}
        />
        
        <WorkflowSection
          title={filteredWorkflowData.proposed.title}
          subtitle={filteredWorkflowData.proposed.subtitle}
          count={filteredWorkflowData.proposed.count}
          tickets={filteredWorkflowData.proposed.tickets}
          type="proposed"
          onAction={handleTicketAction}
          expandedAlerts={expandedAlerts}
          onAlertToggle={handleAlertToggle}
          renderAlertDetails={renderAlertDetails}
        />
        
        <WorkflowSection
          title={filteredWorkflowData.completed.title}
          subtitle={filteredWorkflowData.completed.subtitle}
          count={filteredWorkflowData.completed.count}
          tickets={filteredWorkflowData.completed.tickets}
          type="completed"
          onAction={handleTicketAction}
          expandedAlerts={expandedAlerts}
          onAlertToggle={handleAlertToggle}
          renderAlertDetails={renderAlertDetails}
        />
      </div>

      {/* Remediate Modal */}
      <RemediateModal
        isOpen={remediateModal.isOpen}
        onClose={handleRemediateModalClose}
        ticket={remediateModal.ticket}
        onApprove={handleRemediateApprove}
        isLoading={remediateModal.isLoading}
      />
    </div>
  );
};

export default ActionCenter;